import { Card, CardColor, CompartmentCard } from "@/src/components/cards";
import { openConfirmDialog, openDrawer } from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import { openSinglePickerList } from "@/src/components/singlePicker";
import {
  Consultation,
  DocumentTemplate,
  FieldAnswer,
  FolderItem,
  Hospitalisation,
  Patient,
  PatientDocument,
  PatientDocumentLight,
  SummaryData,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, getUserShortName, openToast, uid } from "@/src/utils/utils";
import moment from "moment";
import React from "react";
import { HospitalisationBilletForm } from "../admission/billet";
import { openDocumentForm, openEditor } from "./documentsHook";
import { DocumentTemplateChoiceList } from "./documentTemplates";
import { HtmlDiv } from "./htmlDiv";

import { generateHospitalisationSummaryHtml } from "./hospitalisationSummary";
import { openHospitalisationSummaryForm } from "./hospitalisationSummaryForm";
import { generateOrientationLetterHtml } from "./orientationLetter";
import { generateConsultationSummaryHtml } from "./consultationSummary";

function DocumentsCompartment({
  patient,
  parent,
  color,
  referenceId,
  fieldAnswers,
}: {
  patient: Patient;
  parent: FolderItem;
  color?: CardColor;
  referenceId?: string;
  fieldAnswers?: (FieldAnswer | null | undefined)[];
}) {
  const [reload, setReload] = React.useState(0);

  async function choseDocumentTemplate({ patient }: { patient: Patient }) {
    if (referenceId) {
      let documentTemplates = await apiFetch("documentTemplates", "loadMany", {
        types: ["standard", "letter"],
        referenceId: referenceId,
      });
      if (documentTemplates.length == 0) {
        openToast("error", "Aucun modèle de document trouvé");
        return;
      }
      if (documentTemplates.length == 1) {
        return await onSelectDocument({
          template: documentTemplates[0],
          patient,
          parent,
        });
      }
      await openSinglePickerList({
        title: "Choisir un modèle de document",
        queryKey: [
          "documentTemplates",
          "loadMany",
          "reference",
          referenceId,
          getItemId(patient),
          getItemId(parent),
        ],
        current: null,
        loadItems: async () => {
          return documentTemplates;
        },
        getChecboxCardContent: (template) => {
          return <div>{template.name}</div>;
        },
        onSelect: async (template) => {
          if (!template) return;
          await onSelectDocument({ template, patient, parent });
        },
      });
      return;
    }

    await openDrawer({
      id: "documentTemplateChoiceDrawer",
      title: "Choisir un modèle de document",
      content: (
        <DocumentTemplateChoiceList
          types={["standard", "letter"]}
          onSelect={async (template) => {
            await onSelectDocument({ template, patient, parent });
          }}
        />
      ),
    });
  }

  async function onSelectDocument({
    template,
    patient,
    parent,
  }: {
    template: DocumentTemplate;
    patient: Patient;
    parent: FolderItem;
  }) {
    let name = template.name.toLowerCase();
    if (name == "lettre d'orientation") {
      template.title ||= "Lettre d'orientation";
      template.content = await generateOrientationLetterHtml({
        patient,
      });
    }

    if (name == "compte rendu" && parent.type == "hospitalisation") {
      template = await generateHospitalisationCompteRenduTemplate({
        template,
        parent,
        patient,
      });
    }

    if (name == "compte rendu" && parent.type == "consultation") {
      template = await generateConsultationCompteRenduTemplate({
        template,
        parent,
        patient,
      });
    }

    let patientDocument: PatientDocument = {
      patient: patient,
      creator: USER?.get(),

      template: template,
      parent,
      content: template.content,
      title: template.title || "Nouveau document",
      status: "pending",
      refAnswer: {
        creator: USER?.get(),
        reference: template.reference,
        patient: patient,
        fieldAnswers: fieldAnswers || [],
        notes: "",
        conclusion: "",
      },
    };

    await openDocumentForm({
      patient,
      patientDocument,
    });
  }

  return (
    <CompartmentCard
      title="Documents"
      color={color || "green"}
      onClick={async () => {
        await choseDocumentTemplate({ patient });
        setReload(reload + 1);
      }}
    >
      <FetchableList
        queryKey={["patientDocuments", patient.id, parent?.id, reload]}
        queryFn={apiFetch(
          "patientDocuments",
          "loadMany",
          {
            patientId: patient.id,
            parentId: parent?.id,
          },
          "Erreur lors du chargement des documents"
        )}
      >
        {(patientDocuments) => (
          <div className="c1 gap10">
            {patientDocuments?.map((patientDocument, index) => {
              return (
                <DocumentTile
                  key={uid()}
                  color={color}
                  patientDocument={patientDocument}
                  onEdit={async () => {
                    let fullPatientDocument = await apiFetch(
                      "patientDocuments",
                      "loadOne",
                      {
                        id: patientDocument?.id,
                      },
                      "Erreur lors du chargement du document"
                    );

                    if (!fullPatientDocument)
                      throw new Error("Le Document n'existe pas.");

                    console.log("template", fullPatientDocument.template);

                    fullPatientDocument.parent = parent;
                    openDocumentForm({
                      patient,
                      patientDocument: fullPatientDocument,
                      onChange: () => {
                        setReload(reload + 1);
                      },
                    });
                  }}
                  onClick={async () => {
                    let fullPatientDocument = await apiFetch(
                      "patientDocuments",
                      "loadOne",
                      {
                        id: patientDocument?.id,
                      },
                      "Erreur lors du chargement du document"
                    );
                    if (!fullPatientDocument)
                      throw new Error("Le Document n'existe pas.");
                    fullPatientDocument.parent = parent;

                    if (!fullPatientDocument.template) {
                      fullPatientDocument.template = {
                        title: fullPatientDocument.title || "",
                        content: fullPatientDocument.content,
                        format: "a4",
                        isValidatable: false,
                        creator: USER?.get(),
                        name: "",
                        showHeader: true,
                        showLeftBar: true,
                        barcodeLocation: "none",
                        showPatientIdentity: true,
                        showTitle: true,
                        parent: null,
                      };
                    }

                    openEditor({
                      patient,
                      patientDocument: fullPatientDocument,
                      onChange: () => {
                        setReload(reload + 1);
                      },
                    });
                  }}
                  onDelete={async () => {
                    await openConfirmDialog({
                      id: "deleteDocument",
                      title: "Supprimer le document",
                      content: "Voulez-vous vraiment supprimer ce document?",
                      onConfirm: async () => {
                        await apiFetch(
                          "patientDocuments",
                          "deleteOne",
                          {
                            id: patientDocument.id,
                          },
                          "Erreur lors de la suppression du document"
                        );
                        setReload(reload + 1);
                      },
                    });
                  }}
                ></DocumentTile>
              );
            })}
          </div>
        )}
      </FetchableList>
    </CompartmentCard>
  );
}

function DocumentTile({
  patientDocument,
  color,
  onEdit,
  onClick,
  onDelete,
}: {
  patientDocument: PatientDocumentLight;
  color?: CardColor;
  onEdit: () => void;
  onClick: () => void;
  onDelete: () => void;
}) {
  return (
    <Card
      color={color || "green"}
      className={patientDocument.status == "pending" ? "empty" : ""}
      title={
        <HtmlDiv>
          {patientDocument.title || patientDocument.template?.title || ""}
        </HtmlDiv>
      }
      subTitle={
        <div className="c1 ">
          <div>Rédigé par {getUserShortName(patientDocument.creator)}</div>
          {patientDocument.initialCreator?.id != null && (
            <div>
              Document initial créé par{" "}
              {getUserShortName(patientDocument.initialCreator)}
            </div>
          )}
          {patientDocument.isValidatedBy && (
            <div className="bold black">
              Validé par {getUserShortName(patientDocument.isValidatedBy)}
            </div>
          )}{" "}
          {!patientDocument.isValidatedBy &&
          patientDocument.template?.isValidatable ? (
            <div className="gray bold black">Non validé</div>
          ) : null}
        </div>
      }
      onClick={onClick}
      onEdit={onEdit}
      onDelete={onDelete}
    >
      <div className="gray">
        {moment(patientDocument._date).format("LL HH:mm ")}
      </div>
    </Card>
  );
}

async function generateHospitalisationCompteRenduTemplate({
  template,
  parent,
  patient,
}: {
  template: DocumentTemplate;
  parent: Hospitalisation;
  patient: Patient;
}) {
  let summaryData: SummaryData = {
    conclusion: "",
    doctors: [],
    evolutionItems: [],
    exams: [],
    item: parent,
  };
  await openHospitalisationSummaryForm({
    summaryData,
    patient,
    onChange: (newValue) => {
      summaryData = newValue;
    },
  });
  await openDrawer({
    id: "hospitalisationInfoDrawer",
    title: "Informations supplémentaires",
    content: (
      <HospitalisationBilletForm
        current={parent}
        onChange={(newValue) => {
          parent = newValue;
        }}
      />
    ),
    onConfirm: async (e) => {
      await apiFetch("hospitalisations", "addOne", {
        hospitalisation: parent as Hospitalisation,
      });
      summaryData.item = parent;
    },
  });

  template.title ||= "Compte rendu";
  template.content = await generateHospitalisationSummaryHtml({ summaryData });
  return template;
}

async function generateConsultationCompteRenduTemplate({
  template,
  parent,
  patient,
}: {
  template: DocumentTemplate;
  parent: Consultation;
  patient: Patient;
}) {
  let summaryData: SummaryData = {
    conclusion: "",
    doctors: [],
    evolutionItems: [],
    exams: [],
    item: parent,
  };

  template.title ||= "Compte rendu";
  template.content = await generateConsultationSummaryHtml({
    consultation: parent,
    patient,
  });
  return template;
}

export { DocumentsCompartment, DocumentTile };
